import { useEffect } from "react";

interface PageMetaOptions {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  jsonLd?: Record<string, unknown>[];
  noIndex?: boolean;
}

function getSiteUrl(): string {
  if (typeof import.meta !== "undefined" && import.meta.env?.VITE_SITE_URL) {
    return import.meta.env.VITE_SITE_URL;
  }
  return "https://oopbuyspreadsheet.ink";
}

function upsertMeta(attr: string, attrValue: string, content: string) {
  const selector =
    attr === "property"
      ? `meta[property="${attrValue}"]`
      : `meta[name="${attrValue}"]`;
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function usePageMeta(options: PageMetaOptions) {
  const {
    title,
    description,
    keywords,
    canonicalPath,
    ogImage,
    ogType = "website",
    jsonLd,
    noIndex = false,
  } = options;

  useEffect(() => {
    const siteUrl = getSiteUrl();
    const fullUrl = canonicalPath
      ? `${siteUrl}${canonicalPath}`
      : `${siteUrl}/`;

    document.title = title;
    upsertMeta("name", "description", description);

    if (keywords) {
      upsertMeta("name", "keywords", keywords);
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", fullUrl);

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", fullUrl);
    upsertMeta("property", "og:type", ogType);
    if (ogImage) {
      upsertMeta("property", "og:image", ogImage);
    }

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);

    if (noIndex) {
      upsertMeta("name", "robots", "noindex, nofollow");
    } else {
      const robotsEl = document.querySelector('meta[name="robots"]');
      if (robotsEl) robotsEl.remove();
    }

    document
      .querySelectorAll('script[type="application/ld+json"][data-readdy-seo]')
      .forEach((el) => el.remove());

    if (jsonLd && jsonLd.length > 0) {
      jsonLd.forEach((ld) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-readdy-seo", "true");
        script.textContent = JSON.stringify(ld);
        document.head.appendChild(script);
      });
    }

    return () => {
      document
        .querySelectorAll(
          'script[type="application/ld+json"][data-readdy-seo]',
        )
        .forEach((el) => el.remove());
    };
  }, [
    title,
    description,
    keywords,
    canonicalPath,
    ogImage,
    ogType,
    jsonLd,
    noIndex,
  ]);
}